import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n';

interface Heading {
  id: string;
  text: string;
  level: 2 | 3;
}

/**
 * Right-rail table of contents for the current article. Scans h2/h3 in
 * `.docs-article` after each route change and renders anchor links with
 * scroll-spy highlighting via IntersectionObserver.
 *
 * Heading ids come from rehype-slug (wired in vite.config.ts), which slugs
 * the heading text into a stable, predictable id.
 */
export default function PageToc() {
  const t = useTranslation();
  const location = useLocation();
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  // Scan headings each time the article subtree settles. MDX loads
  // asynchronously via dynamic-import in DocPage, so on a route change
  // the DOM passes through several intermediate states: old MDX removed,
  // "Loading…" inserted, new MDX inserted. A naive immediate scan or a
  // one-shot setTimeout races with these transitions and can pick up
  // stale headings from the previous page.
  //
  // The fix: debounce a MutationObserver so rapid mutations during a
  // route change are coalesced into a single scan after the DOM stops
  // changing for a beat.
  useEffect(() => {
    setHeadings([]);
    setActiveId('');

    const slugify = (text: string): string =>
      text
        .toLowerCase()
        .trim()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')   // strip combining diacritics
        .replace(/[^\w\s-]/g, '')          // strip punctuation
        .replace(/\s+/g, '-')              // spaces -> dashes
        .replace(/-+/g, '-');

    const article = document.querySelector('.docs-article');
    if (!article) return;

    const scan = () => {
      const heads = Array.from(
        article.querySelectorAll<HTMLHeadingElement>('h2, h3'),
      );
      // Fallback slug if rehype-slug didn't run for some reason.
      for (const h of heads) {
        if (!h.id && h.textContent) h.id = slugify(h.textContent);
      }
      const items: Heading[] = heads
        .filter((h) => h.id && h.textContent)
        .map((h) => ({
          id: h.id,
          text: (h.textContent ?? '').trim(),
          level: h.tagName === 'H2' ? 2 : 3,
        }));
      setHeadings(items);
    };

    let debounceTimer: number | null = null;
    const scheduleScan = () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        scan();
      }, 120);
    };

    const observer = new MutationObserver(scheduleScan);
    observer.observe(article, { childList: true, subtree: true });

    // Kick off an initial scan in case content is already mounted (e.g.
    // initial page load, no route change yet).
    scheduleScan();

    return () => {
      observer.disconnect();
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
    };
  }, [location.pathname]);

  // Scroll-spy: highlight the topmost heading currently in view.
  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      // Activate when a heading enters the upper portion of the viewport.
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    for (const h of headings) {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav className="docs-toc" aria-label={t('toc.onThisPage')}>
      <p className="docs-toc-title">{t('toc.onThisPage')}</p>
      <ul className="docs-toc-list">
        {headings.map((h) => (
          <li
            key={h.id}
            className={`docs-toc-item docs-toc-l${h.level}${activeId === h.id ? ' is-active' : ''}`}
          >
            <a href={`#${h.id}`}>{h.text}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
